import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { NumberParam, StringParam, useQueryParam, withDefault } from 'use-query-params';
import { format, subDays } from 'date-fns';
import { saveAs } from 'file-saver';

import { Button, Input, Pagination } from '@app/components';
import { EmailApi } from '@app/domain/email/api/EmailApi';

import { EmailLogTable } from './components/EmailLogTable';

const DATE_FORMAT = 'yyyy-MM-dd';
const PAGE_SIZE = 25;

// admin-emailbeheer blok B (opdracht §6, Scherm 3 - Verzendlog). Filters on mail type, category,
// status, locale, period and recipient - all in the URL, same pattern as the existing email
// reports table (AdminEmailListPage).
export const AdminEmailLogPage: React.FC = () => {
  const [from, setFrom] = useQueryParam('from', withDefault(StringParam, format(subDays(new Date(), 30), DATE_FORMAT)));
  const [to, setTo] = useQueryParam('to', withDefault(StringParam, format(new Date(), DATE_FORMAT)));
  const [mailKey, setMailKey] = useQueryParam('mailKey', withDefault(StringParam, ''));
  const [status, setStatus] = useQueryParam('status', withDefault(StringParam, ''));
  const [recipientEmail, setRecipientEmail] = useQueryParam('recipient', withDefault(StringParam, ''));
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1));

  const [exporting, setExporting] = useState(false);

  const filterParams = useMemo(
    () => ({
      from: from as string,
      to: to as string,
      mailKey: (mailKey as string) || undefined,
      status: (status as string) || undefined,
      recipientEmail: (recipientEmail as string) || undefined,
    }),
    [from, to, mailKey, status, recipientEmail]
  );

  const logQuery = useQuery(['email-log', filterParams, page], () =>
    EmailApi.adminGetEmailLog({ ...filterParams, page: (page as number) - 1, size: PAGE_SIZE })
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await EmailApi.adminExportEmailLogCsv(filterParams);
      saveAs(blob, `email-log-${from}-${to}.csv`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-end gap-4">
        <Button onClick={handleExport} loading={exporting} variant="outlined" size="small">
          Export CSV
        </Button>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-md bg-accept-light px-3.5 py-2.5 text-[13px] text-accept-text">
        <span>&#10003;</span>
        <span>
          <b>Logs are kept indefinitely.</b> The 30-day cleanup job has been removed.
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3 pt-4">
        <Input
          label="From"
          type="date"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setPage(1);
          }}
        />
        <Input
          label="To"
          type="date"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setPage(1);
          }}
        />
        <Input
          label="Mail key"
          value={mailKey}
          onChange={(e) => {
            setMailKey(e.target.value);
            setPage(1);
          }}
        />
        <Input
          label="Status"
          placeholder="sent, delivered, bounced, skipped..."
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        />
        <Input
          label="Recipient"
          placeholder="name@example.com"
          value={recipientEmail}
          onChange={(e) => {
            setRecipientEmail(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <EmailLogTable rows={logQuery.data?.result ?? []} loading={logQuery.isFetching} />
        {/* Pagination's own clamp-to-pagesCount effect treats "total not loaded yet" the same
            as "genuinely zero results" and forces page back to 0 - only render it once the
            first page of real data (and a real total) has arrived, same guard TableCard and
            MyQrCodes.page already use for this same shared component. Without this, the very
            first mount raced that effect and could park the query on an invalid page (found
            2026-08-24: "Send log" needed two clicks to open, since it's the only screen in this
            feature area that uses pagination at all). */}
        {!!logQuery.data?.total && (
          <Pagination
            currentPage={page as number}
            pageSize={PAGE_SIZE}
            total={logQuery.data.total}
            onPagination={(newPage) => setPage(newPage)}
          />
        )}
      </div>
    </>
  );
};
